import { FunctionComponent, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  BannerSection,
  OverviewSection,
  HomePageCardSectionStyled,
  ProcessExpositionSection,
  PaymentsOverviewSection,
  FaqsSection,
  OkBtn,
} from "./styles";

import HomePageBanner from "./sections/HomePageBanner";
import HomepageOverview from "./sections/HomepageOverview";
import OffersContainer from "./sections/OffersContainer";
import ProcessExposition from "./sections/ProcessExposition";
import FaqsContainer from "./sections/FaqsContainer";

import PaymentsOverview from "containers/PaymentsOverview";

import { activeOffers, purchasedOffers, offersState, availableOffers } from "state/offers-slice";
import { logoutUserFromAPI, setMustSelectContract, userState } from "state/user-slice";
import { setModal, redirectToID } from "state/config-slice";
import { CustomerType } from "types/Customer";
import { isFeatureActive } from "utils/getFeature";
import useErrorCode from "hooks/useErrorCode";

const Home: FunctionComponent = () => {
  const { t } = useTranslation();
  const currentUser = useSelector(userState);
  const offers = useSelector(offersState);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const allOffers = useSelector(availableOffers);
  const currentPurchasedOffers = useSelector(purchasedOffers);
  const currentActiveOffers = useSelector(activeOffers);
  const paymentOverviewFeatureActive = isFeatureActive("Enabled paymentOverview");

  useErrorCode();

  useEffect(() => {
    if (currentUser.mustSelectContract) {
      if (currentUser.userInfo?.accountInformations?.length === 0) {
        dispatch(
          setModal({
            showModal: true,
            errorModal: true,
            title: t("This subscription is only available for cable contract customers"),
            description: t("Please log in again to see if you are eligible for any mobile subscriptions"),
            customButtons: (
              <OkBtn
                id="noContractOkModal"
                title={t("Ok")}
                onClick={async () => {
                  await dispatch(logoutUserFromAPI());
                  dispatch(redirectToID(null));
                  dispatch(setModal({ showModal: false }));
                }}
              />
            ),
          }),
        );
      } else if (allOffers.length === 0) {
        dispatch(setMustSelectContract(false));
      }
    }
  }, [
    allOffers.length,
    currentUser.mustSelectContract,
    currentUser.userInfo?.accountInformations?.length,
    dispatch,
    t,
  ]); // Close the useEffect function

  useEffect(() => {
    const currentChargingId = currentUser.choosenChargingId
      ? currentUser.choosenChargingId.chargingId
      : currentUser.userInfo.chargingId?.chargingId;

    const isCableCustomer =
      currentUser.userInfo.customerType?.toUpperCase() === CustomerType.broadband ||
      currentUser.userInfo.customerType?.toUpperCase() === CustomerType.can ||
      currentUser.userInfo.customerType?.toUpperCase() === CustomerType.uan ||
      currentUser.userInfo.customerType?.toUpperCase() === CustomerType.msisdn;

    const isNoOffersAvailable = isFeatureActive("Custom NotElligible");

    if (isCableCustomer && offers.offersLoaded && allOffers.length === 0) {
      if (isNoOffersAvailable) {
        navigate("/not-eligible-user");
      } else {
        dispatch(
          setModal({
            showModal: true,
            errorModal: true,
            size: "large",
            title: `${t("Sorry, there are no offers currently available for contract number:")} ${currentChargingId}`,
            customButtons: (
              <OkBtn
                id="noCableOfferOkModal"
                title={t("Ok")}
                onClick={async () => {
                  await dispatch(logoutUserFromAPI());
                  dispatch(redirectToID(null));
                  dispatch(setModal({ showModal: false }));
                }}
              />
            ),
          }),
        );
      }
    }
  }, [
    allOffers.length,
    currentUser.choosenChargingId,
    currentUser.userInfo.chargingId?.chargingId,
    currentUser.userInfo.customerType,
    dispatch,
    offers.offersLoaded,
    t,
    navigate,
  ]);

  return (
    <>
      <BannerSection id="homepage-banner">
        <HomePageBanner />
      </BannerSection>

      {!currentUser.loggedIn && (
        <OverviewSection id="homepage-overview" data-testid="homepage-overview">
          <HomepageOverview />
        </OverviewSection>
      )}
      {currentUser.loggedIn && (
        <HomePageCardSectionStyled id="homepage-card-grid">
          <OffersContainer />
        </HomePageCardSectionStyled>
      )}
      {currentUser.loggedIn && currentActiveOffers.length > 0 && paymentOverviewFeatureActive && (
        <PaymentsOverviewSection>
          <PaymentsOverview title={t("Total this month")} offers={currentActiveOffers} />
        </PaymentsOverviewSection>
      )}
      {currentPurchasedOffers.length === 0 && (
        <ProcessExpositionSection>
          <ProcessExposition />
        </ProcessExpositionSection>
      )}

      <FaqsSection>
        <FaqsContainer />
      </FaqsSection>
    </>
  );
};

export default Home;
